// Third-party
import Grid from '@mui/material/Grid2';

// Components
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { TasklistTotals } from './components/TasklistTotals';
import { TasklistsNav } from './components/TasklistsNav';
import { TasklistBlock } from './components/TasklistBlock';
import { MobileTasklistsNav } from './components/MobileTasklistsNav';
import { useCommon } from '../../../hooks/useCommon';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { getTasklists } from '../../../store/tasklists/selectors';
import { tasklistsActions } from '../../../store/tasklists/slice';
import { TTasklist } from '../../../types';
import { MemoizedFilterTabs } from '../../../components/base';
import { INDEX_TO_COLOR } from '../../../data';

const TasklistTab = () => {
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const { t } = useTranslation('common');
  const tasklists = useAppSelector(getTasklists);
  const { locationsByPermissions } = useCommon();

  const dispatch = useAppDispatch();

  const getLengthByStatus = (locationId: number) => tasklists.reduce(
    (count, item) => (item.locations.some(({ id }) => id === locationId) ? count + 1 : count),
    0,
  );

  const tabs = locationsByPermissions.map((location, index) => ({
    value: location.id,
    label: location.name,
    color: INDEX_TO_COLOR[index],
    count: getLengthByStatus(location.id),
  }));

  tabs.unshift({
    value: 0,
    label: t('all', { ns: 'common' }),
    color: 'info',
    count: tasklists.length,
  });

  const handleFilterStatus = (newValue: number) => {
    dispatch(tasklistsActions.setTasklistSelectedLocationId(newValue));

    // Initialize filteredTasklists based on newValue
    let filteredTasklists = tasklists;

    // If newValue is greater than 0, filter tasklists by location
    if (newValue > 0) {
      filteredTasklists = tasklists.filter((tasklist) => tasklist.locations.some((location) => location.id === newValue));
    }

    // Dispatch the first filtered tasklist if it exists, otherwise dispatch an empty tasklist
    if (filteredTasklists.length) {
      dispatch(tasklistsActions.setSelectedTasklist({ ...filteredTasklists[0] }));
    } else {
      dispatch(tasklistsActions.setSelectedTasklist({} as TTasklist));
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid
        size={{
          xs: 12,
          lg: 8,
          md: 8,
        }}
      >
        <Box pb={2}>
          <MemoizedFilterTabs isDisplayBackground={false} tabs={tabs} onFilterChange={handleFilterStatus} />
        </Box>
        <TasklistTotals />
        <TasklistBlock />
      </Grid>
      {!isMobile && (
      <Grid size={{
        lg: 4,
        md: 4,
      }}
      >
        <TasklistsNav />
      </Grid>
      )}
      <MobileTasklistsNav />
    </Grid>
  );
};

export {
  TasklistTab,
};
